/**
 * 安全的 JSON 解析器，处理大整数精度丢失问题
 * 将可能超过 JavaScript Number.MAX_SAFE_INTEGER 的整数自动转换为字符串
 */

// JavaScript 安全整数范围
const MAX_SAFE_INTEGER = Number.MAX_SAFE_INTEGER || 9007199254740991
const MIN_SAFE_INTEGER = Number.MIN_SAFE_INTEGER || -9007199254740991

/**
 * 检查数字是否在安全范围内
 */
function isSafeInteger(num: number): boolean {
  return Number.isInteger(num) && num >= MIN_SAFE_INTEGER && num <= MAX_SAFE_INTEGER
}

/**
 * 安全的 JSON 解析，自动将大整数转换为字符串
 * 在JSON.parse之前预处理字符串，避免JavaScript数字解析导致的精度丢失
 */
export function parseJSONSafely(text: string): any {
  try {
    // 首先尝试预处理JSON字符串，将潜在的大整数用引号包围
    const preprocessedText = preprocessBigInts(text)
    
    return JSON.parse(preprocessedText, (key, value) => {
      // 如果值是数字且超出安全范围，转换为字符串
      if (typeof value === 'number' && !isSafeInteger(value)) {
        console.warn(`检测到不安全的大整数 ${key}: ${value}，已转换为字符串`)
        return String(value)
      }
      return value
    })
  } catch (error) {
    // 如果预处理失败，回退到普通解析
    console.warn('大整数预处理失败，回退到普通JSON解析:', error)
    return JSON.parse(text)
  }
}

/**
 * 预处理JSON字符串，将大整数用引号包围以避免精度丢失
 */
function preprocessBigInts(jsonString: string): string {
  // 处理键值对中的大整数: "key": 1234567890123456789
  let result = jsonString.replace(
    /"([^"]*)":\s*(\d{16,})(\s*[,}])/g,
    (match, key, value, suffix) => {
      const num = parseInt(value, 10)
      if (num > Number.MAX_SAFE_INTEGER || num < Number.MIN_SAFE_INTEGER) {
        console.log(`预处理键值对大整数: "${key}": ${value} -> "${key}": "${value}"`)
        return `"${key}": "${value}"${suffix}`
      }
      return match
    }
  )
  
  // 处理数组/对象中的大整数: [1234567890123456789] 或者 ,1234567890123456789,
  result = result.replace(
    /([\[,\s])(\d{16,})(\s*[,\]\}])/g,
    (match, prefix, value, suffix) => {
      const num = parseInt(value, 10)
      if (num > Number.MAX_SAFE_INTEGER || num < Number.MIN_SAFE_INTEGER) {
        console.log(`预处理数组/对象大整数: ${value} -> "${value}"`)
        return `${prefix}"${value}"${suffix}`
      }
      return match
    }
  )
  
  return result
}

/**
 * 测试大整数预处理功能
 */
export function testBigIntPreprocessing(): void {
  const testCases = [
    {
      name: '键值对中的大整数',
      input: '{"id": 1955445903861481475, "name": "test"}',
      expected: '{"id": "1955445903861481475", "name": "test"}'
    },
    {
      name: '数组中的大整数', 
      input: '[1955445903861481475, "test", 123]',
      expected: '["1955445903861481475", "test", 123]'
    },
    {
      name: '混合场景',
      input: '{"users": [{"id": 1955445903861481475, "roleId": 1234567890123456789}]}',
      expected: '{"users": [{"id": "1955445903861481475", "roleId": "1234567890123456789"}]}'
    }
  ]
  
  console.log('=== 大整数预处理测试 ===')
  
  testCases.forEach((testCase, index) => {
    console.log(`\n测试 ${index + 1}: ${testCase.name}`)
    console.log('输入:', testCase.input)
    
    try {
      const preprocessed = preprocessBigInts(testCase.input)
      console.log('预处理结果:', preprocessed)
      
      const parsed = JSON.parse(preprocessed)
      console.log('解析结果:', parsed)
      
      // 验证ID是否为字符串
      if (parsed.id) {
        console.log(`ID类型: ${typeof parsed.id}, 值: ${parsed.id}`)
        console.log(`✅ ${typeof parsed.id === 'string' ? '成功' : '失败'}: ID保持为字符串`)
      }
      
      if (parsed.users && parsed.users[0]) {
        const user = parsed.users[0]
        console.log(`用户ID类型: ${typeof user.id}, 值: ${user.id}`)
        console.log(`✅ ${typeof user.id === 'string' ? '成功' : '失败'}: 用户ID保持为字符串`)
      }
      
    } catch (error) {
      console.error('❌ 测试失败:', error)
    }
  })
  
  console.log('\n=== 测试完成 ===')
}

/**
 * 扩展 Response 对象，提供安全的 JSON 解析方法
 */
export async function safeJsonParse(response: Response): Promise<any> {
  const text = await response.text()
  return parseJSONSafely(text)
}

/**
 * 递归转换对象中的大整数为字符串
 * 主要用于处理已经解析但可能包含大整数的对象
 */
export function convertBigIntsToStrings(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj
  }
  
  if (typeof obj === 'number') {
    return isSafeInteger(obj) ? obj : String(obj)
  }
  
  if (typeof obj === 'string' || typeof obj === 'boolean') {
    return obj
  }
  
  if (Array.isArray(obj)) {
    return obj.map(convertBigIntsToStrings)
  }
  
  if (typeof obj === 'object') {
    const result: any = {}
    for (const [key, value] of Object.entries(obj)) {
      result[key] = convertBigIntsToStrings(value)
    }
    return result
  }
  
  return obj
}

/**
 * 预定义的需要特别处理的 ID 字段
 */
const ID_FIELDS = ['id', 'userId', 'planId', 'roleId', 'subscriptionId', 'parentId']

/**
 * 智能转换：将指定的 ID 字段转换为字符串
 */
export function convertIdsToStrings(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj
  }
  
  if (Array.isArray(obj)) {
    return obj.map(convertIdsToStrings)
  }
  
  if (typeof obj === 'object') {
    const result: any = {}
    for (const [key, value] of Object.entries(obj)) {
      if (ID_FIELDS.includes(key) && (typeof value === 'number' || typeof value === 'string')) {
        // ID 字段统一转换为字符串
        result[key] = String(value)
      } else {
        result[key] = convertIdsToStrings(value)
      }
    }
    return result
  }
  
  return obj
}
